/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { SkeletonRenderingPreviewResult } from '@agilelab/plugin-wb-builder-catalog';
import { SKELETON_FILE_DIRECTIVE } from '@agilelab/plugin-wb-catalog-extension-common';
import { WbEditor } from '@agilelab/plugin-wb-platform';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import { useEffect, useMemo, useState } from 'react';
import { FileBrowser } from '../../FileBrowser';
import { TaskStatusStepper } from '../../TaskPage/TaskPage';
import { TaskPageLinks } from '../../TaskPage/TaskPageLinks';
import { useDryRun } from '../DryRunContext';
import { DryRunResultsSplitView } from './DryRunResultsSplitView';

// Find the first directory in dry-run output containing either a legacy catalog-info.yaml,
// or a skeleton catalog-info.yaml / parameters.yaml pair for skeleton entities.
function findSkeletonPair(
  directoryContents: Array<{
    path: string;
    base64Content: string;
    executable: boolean;
  }>,
) {
  const byDir = new Map<string, Map<string, string>>();
  for (const file of directoryContents) {
    const lastSlash = file.path.lastIndexOf('/');
    const dir = lastSlash >= 0 ? file.path.slice(0, lastSlash) : '';
    const filename =
      lastSlash >= 0 ? file.path.slice(lastSlash + 1) : file.path;
    if (!byDir.has(dir)) byDir.set(dir, new Map());
    byDir.get(dir)!.set(filename, file.base64Content);
  }
  for (const [, files] of byDir) {
    if (!files.has('catalog-info.yaml')) continue;
    const catalogInfo = atob(files.get('catalog-info.yaml')!);
    const isSkeleton = catalogInfo.startsWith(SKELETON_FILE_DIRECTIVE);
    if (isSkeleton && !files.has('parameters.yaml')) continue;
    return {
      catalogInfo,
      parameters: files.has('parameters.yaml')
        ? atob(files.get('parameters.yaml')!)
        : '',
    };
  }
  return null;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  contentWrapper: {
    flex: 1,
    position: 'relative',
  },
  content: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: 'flex',
    '& > *': {
      flex: 1,
    },
  },
  editor: {
    height: '100%',
    overflowY: 'auto',
  },
  tabs: {
    // inactive tabs inherit ~0.7 opacity by default, dropping below 4.5:1 WCAG AA
    '&& .MuiTab-root': {
      color: theme.palette.text.primary,
      opacity: 1,
    },
  },
  tab: {
    color: theme.palette.text.primary,
    opacity: 1,
  },
}));

function FilesContent() {
  const classes = useStyles();
  const { selectedResult } = useDryRun();
  const [selectedPath, setSelectedPath] = useState<string>('');
  const selectedFile = selectedResult?.directoryContents.find(
    f => f.path === selectedPath,
  );

  useEffect(() => {
    if (selectedResult) {
      const [firstFile] = selectedResult.directoryContents;
      if (firstFile) {
        setSelectedPath(firstFile.path);
      } else {
        setSelectedPath('');
      }
    }
    return undefined;
  }, [selectedResult]);

  if (!selectedResult) {
    return null;
  }
  return (
    <DryRunResultsSplitView>
      <FileBrowser
        selected={selectedPath}
        onSelect={setSelectedPath}
        filePaths={selectedResult.directoryContents.map(file => file.path)}
      />
      <WbEditor
        className={classes.editor}
        height="100%"
        options={{ readOnly: true }}
        value={
          selectedFile?.base64Content ? atob(selectedFile.base64Content) : ''
        }
        language="yaml"
      />
    </DryRunResultsSplitView>
  );
}
function LogContent() {
  const { selectedResult } = useDryRun();
  const [currentStepId, setUserSelectedStepId] = useState<string>();

  const steps = useMemo(() => {
    if (!selectedResult) {
      return [];
    }
    return (
      selectedResult.steps.map(step => {
        const stepLog = selectedResult.log.filter(
          l => l.body.stepId === step.id,
        );
        return {
          id: step.id,
          name: step.name,
          logString: stepLog.map(l => l.body.message).join('\n'),
          status: stepLog[stepLog.length - 1]?.body.status ?? 'completed',
        };
      }) ?? []
    );
  }, [selectedResult]);

  if (!selectedResult) {
    return null;
  }

  const selectedStep = steps.find(s => s.id === currentStepId) ?? steps[0];

  return (
    <DryRunResultsSplitView>
      <TaskStatusStepper
        steps={steps}
        currentStepId={selectedStep.id}
        onUserStepChange={setUserSelectedStepId}
      />
      <WbEditor
        value={selectedStep?.logString ?? ''}
        options={{ readOnly: true }}
      />
    </DryRunResultsSplitView>
  );
}

function OutputContent() {
  const classes = useStyles();
  const { selectedResult } = useDryRun();

  if (!selectedResult) {
    return null;
  }

  return (
    <DryRunResultsSplitView>
      <Box pt={2}>
        {selectedResult.output?.links?.length && (
          <TaskPageLinks output={selectedResult.output} />
        )}
      </Box>
      <WbEditor
        className={classes.editor}
        height="100%"
        options={{ readOnly: true }}
        value={JSON.stringify(selectedResult.output, null, 2)}
        language="yaml"
      />
    </DryRunResultsSplitView>
  );
}

function PreviewContent() {
  const { selectedResult } = useDryRun();

  if (!selectedResult) return null;

  const pair = findSkeletonPair(selectedResult.directoryContents);

  if (!pair) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
        p={2}
      >
        <Typography color="textSecondary" align="center">
          No catalog-info.yaml found in the dry-run output, or the skeleton
          catalog-info.yaml / parameters.yaml pair is missing.
        </Typography>
      </Box>
    );
  }

  return (
    <SkeletonRenderingPreviewResult
      key={selectedResult.id}
      catalogInfo={pair.catalogInfo}
      parameters={pair.parameters}
    />
  );
}

export function DryRunResultsView() {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<
    'files' | 'log' | 'output' | 'preview'
  >('files');

  return (
    <div className={classes.root}>
      <Tabs
        value={selectedTab}
        onChange={(_, v) => setSelectedTab(v)}
        className={classes.tabs}
      >
        <Tab value="files" label="Files" className={classes.tab} />
        <Tab value="log" label="Log" className={classes.tab} />
        <Tab value="output" label="Output Links" className={classes.tab} />
        <Tab value="preview" label="Catalog Preview" className={classes.tab} />
      </Tabs>
      <Divider />

      <div className={classes.contentWrapper}>
        <div className={classes.content}>
          {selectedTab === 'files' && <FilesContent />}
          {selectedTab === 'log' && <LogContent />}
          {selectedTab === 'output' && <OutputContent />}
          {selectedTab === 'preview' && <PreviewContent />}
        </div>
      </div>
    </div>
  );
}
